import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import {
  TanCreateRequest,
  TanCreateResponse,
  TanConfirmRequest,
  TanConfirmResponse,
  TanConfirmRequestV2,
  TanConfirmResponseV2
} from '@/services/mobi-api/handsetDti/types'
import {
  createTanHandset,
  confirmTanHandset,
  confirmTanHandsetV2
} from '@/services/mobi-api/handsetDti'
import useIntl from '@/utils/intl'
import { Loop } from '@mui/icons-material'
import { Divider, TextField } from '@mui/material'
import React, {
  FunctionComponent,
  useEffect,
  useReducer,
  useState
} from 'react'
import OTPInput from 'react-otp-input'
import { Obj } from '..'
import EMongolia from '@/pages/handsetDti/eMongolia'
import { IMG_URL } from '@/utils/constants'

interface Props {
  onClick: (obj: Obj) => void
  back: () => void
  instanceId: string
  initialEmail?: string
  isNoMail?: boolean
  initialPhoneNumber?: string
  isTanCreated?: boolean
  tanRes?: TanCreateResponse['response']
  tanRes2?: TanCreateResponse
  choosenPackage?: string
  responseDescription?: string
  isPhoneTanConfirmed?: boolean
}

interface State {
  loading?: boolean
  buttonLoading?: boolean
  otp?: string
  email?: string
  tanLocked?: boolean
  tanIdentifierLocked?: boolean
  isTanCreated?: boolean
  tanRes?: TanCreateResponse['response']
  tanRes2?: TanCreateResponse
  timerId?: NodeJS.Timer
  errorMessage?: string
  danRedirectUrl?: string
  version?: number
}

const checkEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (email.trim().toLowerCase() === '') {
    return false
  }
  return emailRegex.test(email.trim())
}

const EmailInput: FunctionComponent<Props> = ({
  onClick,
  back,
  instanceId,
  initialEmail,
  isNoMail = false,
  initialPhoneNumber,
  isTanCreated: initialIsTanCreated,
  tanRes: initialTanRes,
  choosenPackage,
  isPhoneTanConfirmed = false
}) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const [counter, setCounter] = useState(0)
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      loading: false,
      buttonLoading: false,
      otp: '',
      email:
        (initialEmail?.toLowerCase() === '' || isNoMail)
          ? ''
          : initialEmail || '',
      tanLocked: false,
      tanIdentifierLocked: false,
      isTanCreated: initialIsTanCreated ?? false,
      tanRes: initialTanRes
    }
  )

  useEffect(() => {
    if (initialEmail && checkEmail(initialEmail) && !isNoMail) {
      setState({ email: initialEmail })
    }
    if (initialIsTanCreated && initialTanRes != null) {
      setState({ isTanCreated: true, tanRes: initialTanRes })
      setCounter(initialTanRes.timeLeftInSec)
      startTimer()
    }
    return () => clearTimer()
  }, [])

  const handleTanCreate = async (): Promise<void> => {
    if (!state.email || !checkEmail(state.email)) {
      showError(translate('error.emailError'))
      return
    }
    try {
      clearTimer()
      setState({ loading: true })
      const req: TanCreateRequest = {
        type: 'email',
        value: state.email,
        instanceId
      }
      const res = await createTanHandset(req)
      if ((res == null) || !res.responseCode) {
        showError(translate('request.failed'))
        return
      }
      const {
        responseCode,
        response,
        responseDescription,
        danRedirectUrl,
        version = 1
      } = res
      if (responseCode !== 'SUCCESS') {
        if (responseCode === 'TAN_CREATION_LOCKED') {
          setState({ tanRes: response, tanLocked: true })
        } else if (responseCode === 'TAN_IDENTIFIER_LOCKED') {
          setState({ tanRes: response, tanIdentifierLocked: true })
        } else {
          showError(responseDescription || translate('request.failed'))
        }
        return
      }
      setState({
        tanRes: response,
        isTanCreated: true,
        version,
        danRedirectUrl
      })
      setCounter(response?.timeLeftInSec)
      startTimer()
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false, otp: '' })
    }
  }

  const handleTanConfirm = async (): Promise<void> => {
    if (!state.otp || state.otp.length < 6) {
      showError(translate('sim.tan_mismatch'))
      return
    }
    try {
      setState({ loading: true })
      let res: TanConfirmResponse | TanConfirmResponseV2 | undefined
      const req = { type: 'email', instanceId, code: state.otp ?? '' }
      if (state.version === 2) {
        res = await confirmTanHandsetV2(req as TanConfirmRequestV2)
      } else if (state.version === 1 || !state.version) {
        res = await confirmTanHandset(req as TanConfirmRequest)
      } else {
        showError(translate('error'))
        return
      }
      if ((res == null) || !res.responseCode) {
        showError(translate('request.failed'))
        return
      }
      const isBlacklist = res.errorMessage === 'BLACKLIST'
      const isUnderage = res.errorMessage === 'UNDERAGE'
      if (res.responseCode === 'SUCCESS') {
        if (isBlacklist || isUnderage) {
          setState({
            danRedirectUrl: res.danRedirectUrl ?? '',
            errorMessage: res.errorMessage
          })
          return
        }

        onClick({
          instanceId,
          email: state.email,
          danUrl: res.danRedirectUrl ?? '',
          phoneNumber: initialPhoneNumber || ''
        })
      } else {
        const msg =
          res.responseCode === 'TAN_MISMATCHED' ||
          res.responseCode === 'TAN_NOT_FOUND'
            ? translate('sim.tan_mismatch')
            : res.responseCode === 'TAN_LOCKED'
              ? translate('sim.tan_locked')
              : translate('request.failed')

        showError(msg)
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false })
    }
  }
  const startTimer = (): void => {
    const intervalId = setInterval(() => {
      setCounter((prev) => prev - 1)
    }, 1000)
    setState({ timerId: intervalId })
  }

  const clearTimer = (): void => {
    clearInterval(state.timerId)
    setState({ timerId: undefined })
    setCounter(0)
  }

  const renderTimer = (): string => {
    if (counter !== undefined && counter > 0) {
      const min = Math.floor(counter / 60)
      const sec = Math.floor(counter % 60)
      const minStr = min < 10 ? '0' + min.toString() : min.toString()
      const secStr = sec < 10 ? '0' + sec.toString() : sec.toString()

      return minStr + ':' + secStr
    } else {
      return '00:00'
    }
  }

  const renderDuration = (time: number | undefined): string => {
    if (time === undefined || time === null) {
      return '?'
    }
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time - hours * 3600) / 60)
    const minutesStr: string =
      minutes < 10 ? '0' + minutes.toString() : minutes.toString()

    if (locale === 'en') {
      return hours > 0
        ? hours.toString() + ' hours and ' + minutesStr + ' minutes'
        : minutesStr + ' minutes'
    } else {
      return hours > 0
        ? hours.toString() + ' цаг ' + minutesStr + ' минутын '
        : minutesStr + ' минутын '
    }
  }

  if (state.loading) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
        <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 items-center justify-center'>
          <Loader />
        </div>
        <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <div className='md:w-96 w-full' />
          <DarkButton type='outlined' onClick={back} fullwidth>
            {translate('back')}
          </DarkButton>
        </div>
      </div>
    )
  }

  if (state.tanLocked || state.tanIdentifierLocked) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
        <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 items-center justify-center'>
          <p className='text-center w-2/3 text-textMid'>
            {translate('sim.tan_creation_locked').replace('$', renderDuration(state.tanRes?.creationLimit.lockTimeLeftSec))}
          </p>
        </div>
      </div>
    )
  }

  if (state.errorMessage === 'UNDERAGE' || state.errorMessage === 'BLACKLIST') {
    return (
      <EMongolia
        onClick={onClick}
        danRedirectUrl={state.danRedirectUrl ?? ''}
        errorType={state.errorMessage}
        back={back}
      />
    )
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start relative no-scrollbar'>
      <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4'>
        {state.isTanCreated
          ? (
            <div className='flex flex-col items-center justify-center gap-4 w-full max-w-md'>
              <div className='w-full'>
                <TextField
                  label={translate('email')}
                  variant='outlined'
                  size='small'
                  className='w-full rounded-lg mt-4 text-textHigh'
                  value={state.email}
                  InputLabelProps={{
                    style: { color: 'var(--color-text-textHigh)' },
                    shrink: true
                  }}
                  InputProps={{
                    readOnly: true,
                    style: {
                      color: 'var(--color-text-textHigh)'
                    }
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '8px'
                    }
                  }}
                />
              </div>
              {choosenPackage !== 'option2' && (
                <div
                  className='w-full max-w-md box-box-border flex items-center gap-2 mt-4 border border-[#F4A610] bg-[#FEFCF5] rounded-lg p-2'
                  role='alert'
                >
                  <img
                    width={32}
                    height={30}
                    draggable={false}
                    src={IMG_URL + '/uploads/icons/warning.svg'}
                    alt='warning'
                    className='ml-3'
                  />
                  <span
                    className='text-sm overflow-hidden break-words'
                    style={{ color: 'rgba(120, 53, 15, 1)' }}
                  >
                    {state.version === 2
                      ? translate('leasing.register.email')
                      : translate('leasing.warning')}
                  </span>
                </div>
              )}
              <p className='text-textMid font-medium text-sm text-center pt-24 whitespace-pre-line'>
                {state.tanRes2?.responseDescription.includes('Tan sent to') &&
              !state.tanRes2?.responseDescription.includes('@')
                  ? translate('leasing.register.email')
                  : translate('leasing.tan_sent').replace(
                    '$email',
                    state.email ?? ''
                  )}
              </p>
              <div className='w-full flex justify-center h-fit'>
                <OTPInput
                  value={state.otp ?? ''}
                  onChange={(otp: string) => setState({ otp })}
                  numInputs={6}
                  renderSeparator={<Divider orientation='vertical' />}
                  renderInput={(props) => (
                    <input
                      {...props}
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                    />
                  )}
                  inputStyle={{
                    width: '45px',
                    height: '45px',
                    fontSize: '18px',
                    fontWeight: '700',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-surfaceInverse, #ffffff)'
                  }}
                  containerStyle={{
                    border: '1px solid #CFD3DB',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'var(--color-surfaceInverse, #ffffff)'
                  }}
                />
              </div>
              {(state.tanRes != null) && (
                <span className='text-textHigh text-sm'>{renderTimer()}</span>
              )}
              <DarkButton
                type='outlined'
                onClick={() => {
                  void handleTanCreate()
                }}
                leadingIcon={<Loop />}
              >
                {translate('sim.tan.getagain')}
              </DarkButton>
            </div>
            )
          : (
            <div className='w-full flex flex-col gap-2'>
              <TextField
                label={translate('email')}
                variant='outlined'
                size='small'
                className='w-full rounded-lg mt-4 text-textHigh'
                value={state.email}
                onChange={(e) => setState({ email: e.target.value })}
                InputLabelProps={{
                  style: { color: 'var(--color-text-textHigh)' }
                }}
                InputProps={{
                  style: {
                    color: 'var(--color-text-textHigh)'
                  }
                }}
                sx={{
                  '& fieldset': {
                    borderRadius: '8px',
                    borderColor: checkEmail(state.email ?? '')
                      ? 'var(--color-stroke-secondaryHighStroke)'
                      : 'var(--color-error-error)'
                  }
                }}
              />
            </div>
            )}
      </div>
      <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96 w-full' />
        {state.isTanCreated
          ? (
            <DarkButton
              onClick={handleTanConfirm}
              fullwidth
              disabled={!state.otp || state.otp.length < 6}
            >
              {translate('continue')}
            </DarkButton>
            )
          : (
            <DarkButton
              onClick={handleTanCreate}
              disabled={!checkEmail(state.email ?? '')}
              fullwidth
            >
              {translate('continue')}
            </DarkButton>
            )}
      </div>
    </div>
  )
}

export default EmailInput
